import { Footprints, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import {
  nomeDaPeca,
  pecasImportadas,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import { polygonArea } from '@obraplan/core/domain/takeoff/polygon';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  flightRise,
  flightWidth,
  stairFormwork,
  stairProjection,
  stairSteps,
  stairVolume,
  type Stair,
} from '@obraplan/core/domain/takeoff/stair';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { NOME_DA_PECA_MAX, NomeInput, NumeroInput } from './campos';
import {
  caixaDoContorno,
  COR_DA_ESCADA,
  emMetros,
  emTextoDeCentimetros,
  linhasDosDegraus,
  rotuloDaEscada,
  setaDoLance,
} from './escada-da-prancha';
import {
  acoAprovadoDoElemento,
  fatoresDaPecaDaFundacao,
  mediaDoAcoDaPeca,
  taxaDeBaseDaPeca,
} from './fatores-proprios';
import {
  AcoDaPecaDaFundacao,
  falaFeminina,
  FatoresDaPecaDaFundacao,
  Linha,
  NumeroAoTerminar,
} from './FundacaoNaPrancha';
import { metros } from './formato';
import { metresToPixel, type Pixel } from './plan-tracer';
import type { ServicosAprovados } from './servicos-aprovados';
import type { WorkspaceAction } from './typology-draft';
import type {
  ArrastoDaEscada,
  EscadaNaPrancha,
  FerramentaDaEscada,
  ParteDaEscada,
  PecaDaEscada,
} from './use-escada-na-prancha';
import { parteDoAlvo } from './use-escada-na-prancha';

/**
 * A escada sobre a prancha, e os painéis dela (Fase 27, etapa 5 — D10, F17, RN-373).
 *
 * O lance é o contorno preenchido, com as linhas dos degraus e uma seta no sentido da subida; o
 * patamar é o contorno liso; o nome da escada e os degraus ficam no rótulo. As cores não seguem
 * o tema, pelo mesmo motivo das do resto do traçado: a prancha é papel branco.
 */

// ── O desenho ───────────────────────────────────────────────────────────────────

const pontosDe = (vertices: readonly Pixel[]): string =>
  vertices.map((vertice) => `${String(vertice.x)},${String(vertice.y)}`).join(' ');

type Metros = { x: number; y: number };

function Rotulo({ ponto, texto, zoom }: { ponto: Pixel; texto: string; zoom: number }) {
  const fonte = 12 / zoom;
  return (
    <text
      data-rotulo=""
      x={ponto.x}
      y={ponto.y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fonte}
      fontWeight={600}
      fill={COR_DA_ESCADA}
      stroke="#ffffff"
      strokeWidth={fonte * 0.22}
      paintOrder="stroke"
      className="pointer-events-none select-none"
    >
      {texto}
    </text>
  );
}

/** A seta da subida, de uma ponta à outra do lance, com a ponta aberta no fim. */
function Seta({ de, ate, zoom }: { de: Pixel; ate: Pixel; zoom: number }) {
  const dx = ate.x - de.x;
  const dy = ate.y - de.y;
  const tamanho = Math.hypot(dx, dy);
  if (tamanho === 0) return null;
  // Encolhe um pouco das pontas, para a seta não encostar nas bordas do lance.
  const ux = dx / tamanho;
  const uy = dy / tamanho;
  const folga = tamanho * 0.12;
  const inicio = { x: de.x + ux * folga, y: de.y + uy * folga };
  const fim = { x: ate.x - ux * folga, y: ate.y - uy * folga };
  const ponta = 9 / zoom;
  const aberta = 0.5;
  const asa = (sinal: number): Pixel => ({
    x: fim.x - ponta * (ux * Math.cos(aberta) - sinal * uy * Math.sin(aberta)),
    y: fim.y - ponta * (uy * Math.cos(aberta) + sinal * ux * Math.sin(aberta)),
  });
  return (
    <g data-seta="" className="pointer-events-none">
      <line
        x1={inicio.x}
        y1={inicio.y}
        x2={fim.x}
        y2={fim.y}
        stroke={COR_DA_ESCADA}
        strokeWidth={1.6 / zoom}
      />
      <polyline
        points={pontosDe([asa(1), fim, asa(-1)])}
        fill="none"
        stroke={COR_DA_ESCADA}
        strokeWidth={1.6 / zoom}
        strokeLinejoin="round"
      />
    </g>
  );
}

const emPixels = (contorno: readonly Metros[], escala: number): readonly Pixel[] =>
  contorno.map((ponto) => metresToPixel(ponto, escala));

/** O centro da caixa que envolve todos os contornos da escada — onde o nome dela fica. */
function centroDaEscada(escada: Stair): Metros {
  const contornos = [
    ...escada.flights.map((lance) => lance.outline),
    ...escada.landings.map((patamar) => patamar.outline),
  ];
  const caixas = contornos.map(caixaDoContorno);
  const minX = Math.min(...caixas.map((caixa) => caixa.minX));
  const maxX = Math.max(...caixas.map((caixa) => caixa.maxX));
  const minY = Math.min(...caixas.map((caixa) => caixa.minY));
  const maxY = Math.max(...caixas.map((caixa) => caixa.maxY));
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

export function EscadasDesenhadas({
  escadas,
  escala,
  zoom,
  selecionada,
  arrastando,
  clicavel,
  onClicar,
}: {
  escadas: readonly Stair[];
  escala: number;
  zoom: number;
  selecionada: PecaDaEscada | undefined;
  arrastando: ArrastoDaEscada | undefined;
  /** Falso enquanto se traça outra coisa: aí o clique é do traço, e a escada deixa passar. */
  clicavel: boolean;
  onClicar: (id: StructuralPieceId, parte: ParteDaEscada | undefined) => void;
}) {
  if (escadas.length === 0) return null;

  return (
    <g>
      {escadas.map((escada) => {
        const naMao = arrastando?.id === escada.id && arrastando.moveu;
        const dx = naMao ? arrastando.atual.x - arrastando.origem.x : 0;
        const dy = naMao ? arrastando.atual.y - arrastando.origem.y : 0;
        const inteira = selecionada?.tipo === 'ESCADA' && selecionada.id === escada.id;

        return (
          <g
            key={escada.id}
            data-escada={escada.id}
            className={clicavel ? 'cursor-pointer' : 'pointer-events-none'}
            transform={naMao ? `translate(${String(dx)} ${String(dy)})` : undefined}
            onClick={(evento) => {
              if (!clicavel) return;
              evento.stopPropagation();
              onClicar(escada.id, parteDoAlvo(evento.target as Element));
            }}
          >
            <title>
              {`Escada ${escada.name} — ${String(stairSteps(escada))} degraus, ${metros(stairVolume(escada), 3)} m³`}
            </title>
            {escada.landings.map((patamar, indice) => {
              const parteEscolhida =
                selecionada?.tipo === 'PATAMAR' &&
                selecionada.id === escada.id &&
                selecionada.indice === indice;
              return (
                <polygon
                  key={`p-${String(indice)}`}
                  data-patamar={indice}
                  points={pontosDe(emPixels(patamar.outline, escala))}
                  fill={COR_DA_ESCADA}
                  fillOpacity={0.1}
                  stroke={COR_DA_ESCADA}
                  strokeWidth={(parteEscolhida || inteira ? 3.5 : 1.5) / zoom}
                  strokeLinejoin="round"
                />
              );
            })}
            {escada.flights.map((lance, indice) => {
              const parteEscolhida =
                selecionada?.tipo === 'LANCE' &&
                selecionada.id === escada.id &&
                selecionada.indice === indice;
              const contorno = emPixels(lance.outline, escala);
              const seta = setaDoLance(contorno);
              return (
                <g key={`l-${String(indice)}`} data-lance={indice}>
                  <polygon
                    points={pontosDe(contorno)}
                    fill={COR_DA_ESCADA}
                    fillOpacity={0.16}
                    stroke={COR_DA_ESCADA}
                    strokeWidth={(parteEscolhida || inteira ? 3.5 : 1.5) / zoom}
                    strokeLinejoin="round"
                  />
                  {linhasDosDegraus(contorno, lance.steps).map(([a, b], passo) => (
                    <line
                      key={passo}
                      data-degrau=""
                      x1={a.x}
                      y1={a.y}
                      x2={b.x}
                      y2={b.y}
                      stroke={COR_DA_ESCADA}
                      strokeWidth={1 / zoom}
                      className="pointer-events-none"
                    />
                  ))}
                  {seta !== undefined && <Seta de={seta.de} ate={seta.ate} zoom={zoom} />}
                </g>
              );
            })}
            <Rotulo
              ponto={metresToPixel(centroDaEscada(escada), escala)}
              texto={rotuloDaEscada(escada)}
              zoom={zoom}
            />
          </g>
        );
      })}
    </g>
  );
}

/** O que está sendo traçado e ainda não foi cadastrado: o retângulo do lance ou do patamar. */
export function EscadaEmTraco({ escada, zoom }: { escada: EscadaNaPrancha; zoom: number }) {
  const { traco, ferramenta } = escada;
  if (ferramenta === undefined || traco === undefined) return null;
  const tracejado = `${String(4 / zoom)} ${String(3 / zoom)}`;

  return (
    <g className="pointer-events-none">
      {traco.vertices.length >= 3 && (
        <polygon
          data-escada-nova=""
          points={pontosDe(traco.vertices)}
          fill={COR_DA_ESCADA}
          fillOpacity={0.1}
          stroke={COR_DA_ESCADA}
          strokeWidth={2 / zoom}
          strokeDasharray={tracejado}
        />
      )}
      {traco.marcados.map((ponto, indice) => (
        <circle
          key={`e-${String(indice)}`}
          cx={ponto.x}
          cy={ponto.y}
          r={(indice === 0 ? 6 : 4) / zoom}
          fill={COR_DA_ESCADA}
        />
      ))}
    </g>
  );
}

// ── O botão da barra ────────────────────────────────────────────────────────────

/** "Escada": mora na área comum de qualquer pavimento. */
export function BotoesDaEscada({
  ferramenta,
  onEscolher,
}: {
  ferramenta: FerramentaDaEscada | undefined;
  onEscolher: (ferramenta: 'ESCADA') => void;
}) {
  return (
    <Button
      size="sm"
      variant={ferramenta === 'ESCADA' ? 'primary' : 'ghost'}
      onClick={() => onEscolher('ESCADA')}
    >
      <Footprints className="size-4" /> Escada
    </Button>
  );
}

// ── Os painéis da ferramenta ────────────────────────────────────────────────────

function Recusa({ erro }: { erro: string | undefined }) {
  if (erro === undefined) return null;
  return (
    <p role="alert" className="text-xs text-(--color-state-danger)">
      {erro}
    </p>
  );
}

function SituacaoDoTraco({
  escada,
  pronto,
}: {
  escada: EscadaNaPrancha;
  pronto: string | undefined;
}) {
  const { contorno, cantos } = escada;
  return (
    <>
      <span className="text-xs text-(--color-content-muted)">
        {contorno === undefined ? `${String(cantos)} de 2 cantos` : (pronto ?? 'Contorno pronto.')}
      </span>
      {escada.impedimento !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{escada.impedimento}</span>
      )}
    </>
  );
}

function BotoesDoTraco({
  escada,
  rotuloDoBotao,
}: {
  escada: EscadaNaPrancha;
  rotuloDoBotao: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="primary"
        size="sm"
        disabled={escada.contorno === undefined || escada.impedimento !== undefined}
        onClick={escada.adicionar}
      >
        {rotuloDoBotao}
      </Button>
      <Button variant="ghost" size="sm" disabled={escada.cantos === 0} onClick={escada.refazer}>
        Refazer
      </Button>
    </div>
  );
}

const DICA_DO_RETANGULO =
  'Arraste de um canto ao outro — ou clique nos dois cantos. A subida vai do primeiro ao segundo.';

/** Os quatro números do lance: degraus, espelho, piso e espessura. */
function CamposDoLance({ escada }: { escada: EscadaNaPrancha }) {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label="Degraus" htmlFor="escada-degraus">
        <NumeroInput
          id="escada-degraus"
          value={escada.degraus}
          className="w-20"
          onChange={escada.mudarDegraus}
        />
      </Field>
      <Field label="Espelho (cm)" htmlFor="escada-espelho">
        <NumeroInput
          id="escada-espelho"
          value={escada.espelhoEmCm}
          className="w-20"
          onChange={escada.mudarEspelhoEmCm}
        />
      </Field>
      <Field label="Piso (cm)" htmlFor="escada-piso">
        <NumeroInput
          id="escada-piso"
          value={escada.pisoEmCm}
          className="w-20"
          onChange={escada.mudarPisoEmCm}
        />
      </Field>
      <Field label="Espessura (cm)" htmlFor="escada-espessura">
        <NumeroInput
          id="escada-espessura"
          value={escada.espessuraEmCm}
          className="w-20"
          onChange={escada.mudarEspessuraEmCm}
        />
      </Field>
    </div>
  );
}

/** O que o contorno e as medidas dão: a largura que saiu do desenho e a altura que o lance vence. */
const resumoDoLance = (escada: EscadaNaPrancha): string | undefined =>
  escada.largura === undefined || escada.altura === undefined
    ? undefined
    : `Largura ${metros(escada.largura)} m · altura vencida ${metros(escada.altura)} m`;

/** O painel da escada nova: o primeiro lance, o nome e as medidas. */
export function EmEscada({
  escada,
  erro,
  onSelecionar,
  onSair,
}: {
  escada: EscadaNaPrancha;
  /** A última recusa do desenho. */
  erro: string | undefined;
  onSelecionar: (peca: PecaDaEscada) => void;
  onSair: () => void;
}) {
  return (
    <section
      aria-label="Escadas"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">Escada</h3>
      <p className="text-xs text-(--color-content-muted)">
        Desenhe o primeiro lance. {DICA_DO_RETANGULO}
      </p>

      <Field label="Nome da escada" htmlFor="escada-nome">
        <Input
          id="escada-nome"
          maxLength={NOME_DA_PECA_MAX}
          value={escada.nome}
          className="w-28"
          onChange={(evento) => escada.digitarNome(evento.target.value)}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') escada.adicionar();
          }}
        />
      </Field>

      <CamposDoLance escada={escada} />

      {escada.contornoDoAmbiente !== undefined && (
        <Button variant="secondary" size="sm" onClick={escada.usarContornoDoAmbiente}>
          Usar o contorno do ambiente Escada
        </Button>
      )}

      <SituacaoDoTraco escada={escada} pronto={resumoDoLance(escada)} />

      <BotoesDoTraco escada={escada} rotuloDoBotao="Adicionar escada" />

      <Recusa erro={erro} />

      {escada.escadas.length > 0 && (
        <ul aria-label="Escadas já desenhadas" className="flex flex-wrap gap-1">
          {escada.escadas.map((existente) => (
            <li key={existente.id}>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Selecionar ${existente.name}`}
                onClick={() => onSelecionar({ tipo: 'ESCADA', id: existente.id })}
              >
                {rotuloDaEscada(existente)}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

/** O painel do lance novo ou do patamar novo, dentro da escada escolhida. */
export function EmParteDaEscada({
  escada,
  erro,
  onSair,
}: {
  escada: EscadaNaPrancha;
  erro: string | undefined;
  onSair: () => void;
}) {
  const { alvo } = escada;
  const lance = escada.ferramenta === 'LANCE';

  return (
    <section
      aria-label={lance ? 'Lance' : 'Patamar'}
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">
        {lance ? 'Lance' : 'Patamar'}
        {alvo === undefined ? '' : ` em ${alvo.name}`}
      </h3>
      <p className="text-xs text-(--color-content-muted)">
        {alvo === undefined
          ? 'Escolha antes a escada — clique nela na planta.'
          : `${lance ? 'Desenhe o lance' : 'Desenhe o patamar'} de ${alvo.name}. ${DICA_DO_RETANGULO}`}
      </p>

      {lance ? (
        <CamposDoLance escada={escada} />
      ) : (
        <Field label="Espessura do patamar (cm)" htmlFor="patamar-espessura">
          <NumeroInput
            id="patamar-espessura"
            value={escada.espessuraDoPatamarEmCm}
            className="w-24"
            onChange={escada.mudarEspessuraDoPatamarEmCm}
          />
        </Field>
      )}

      <SituacaoDoTraco
        escada={escada}
        pronto={
          escada.contorno === undefined
            ? undefined
            : lance
              ? resumoDoLance(escada)
              : `Patamar pronto: ${metros(polygonArea(escada.contorno))} m²`
        }
      />

      <BotoesDoTraco
        escada={escada}
        rotuloDoBotao={lance ? 'Adicionar lance' : 'Adicionar patamar'}
      />

      <Recusa erro={erro} />

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

// ── A escada selecionada ────────────────────────────────────────────────────────

/** "desta escada", "nesta escada": a escada é feminina, e o card fala assim dela. */
const FALA_DA_ESCADA = falaFeminina('escada');

/**
 * Editar a escada clicada: o nome, as contas dela à vista, as partes, e excluir. "Lance" e
 * "Patamar" ligam o traço de uma parte nova dentro desta escada.
 *
 * Desde a Fase 42 (etapa 3 — G3; RN-571, RN-572, RN-574), o card traz também "Fatores desta
 * escada" e "Aço desta escada (kg/m³)", os mesmos recolhíveis do baldrame: sem tipo, o fator
 * próprio vale por cima do índice da obra, e a taxa própria por cima da da disciplina. A escada
 * da planilha por peça não leva nenhum dos dois, e o card diz por quê.
 *
 * Com a média da estrutura (etapa 4 — RN-584), cada linha de fator e cada bitola mostram a média
 * de hoje ao lado, só para ler, como o baldrame (RN-554).
 */
export function EscadaSelecionada({
  escada,
  erro,
  catalogo,
  pecasDaPlanilha,
  aprovados,
  media,
  dispatch: despachar,
  desenhando,
  onDesenhar,
  onSelecionarParte,
  onFechar,
}: {
  escada: Stair;
  /** A última recusa do desenho — só aparece depois de a pessoa editar **esta** escada. */
  erro: string | undefined;
  /** O cadastro da obra, para os fatores e o aço próprios (Fase 42, RN-571). Ausente, sem eles. */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A planilha por peça: a escada de nome importado não leva fator nem taxa (RN-574). */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  /** Os serviços aprovados no orçamento: o de aço da escada trava a taxa (RN-575). */
  aprovados?: ServicosAprovados | undefined;
  /** A média das obras marcadas: a da estrutura ao lado do fator e da bitola (RN-584). */
  media?: ApartmentAverageForProject | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  desenhando: FerramentaDaEscada | undefined;
  onDesenhar: (ferramenta: 'LANCE' | 'PATAMAR') => void;
  onSelecionarParte: (parte: ParteDaEscada) => void;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const fatores = useMemo(
    () => fatoresDaPecaDaFundacao(catalogo, 'ESCADA', escada, media),
    [catalogo, escada, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, 'ESCADA'), [media]);
  const daPlanilha = pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(escada.name));

  return (
    <section
      aria-label={`Escada ${escada.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Escada {escada.name}</h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <Field label="Nome" htmlFor={`escada-${escada.id}-nome`}>
        <NomeInput
          id={`escada-${escada.id}-nome`}
          value={escada.name}
          className="w-28"
          respostaImediata
          onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
          onCommit={(nome) => dispatch({ type: 'UPDATE_STAIR', pieceId: escada.id, name: nome })}
        />
      </Field>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Lances" valor={String(escada.flights.length)} />
        <Linha rotulo="Patamares" valor={String(escada.landings.length)} />
        <Linha rotulo="Degraus" valor={String(stairSteps(escada))} />
        <Linha rotulo="Projeção em planta" valor={`${metros(stairProjection(escada))} m²`} />
        <Linha rotulo="Concreto" valor={`${metros(stairVolume(escada), 3)} m³`} />
        <Linha rotulo="Fôrma" valor={`${metros(stairFormwork(escada))} m²`} />
      </div>

      <ul aria-label="Partes da escada" className="flex flex-wrap gap-1">
        {escada.flights.map((lance, indice) => (
          <li key={`l-${String(indice)}`}>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Selecionar o lance ${String(indice + 1)}`}
              onClick={() => onSelecionarParte({ tipo: 'LANCE', indice })}
            >
              Lance {indice + 1} · {lance.steps} degraus
            </Button>
          </li>
        ))}
        {escada.landings.map((patamar, indice) => (
          <li key={`p-${String(indice)}`}>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Selecionar o patamar ${String(indice + 1)}`}
              onClick={() => onSelecionarParte({ tipo: 'PATAMAR', indice })}
            >
              Patamar {indice + 1} · {metros(polygonArea(patamar.outline))} m²
            </Button>
          </li>
        ))}
      </ul>

      {catalogo !== undefined && (
        <FatoresDaPecaDaFundacao
          peca={escada}
          rotulo="escada"
          fala={FALA_DA_ESCADA}
          servicos={fatores}
          daPlanilha={daPlanilha}
          semFator="não leva fator próprio"
          rotuloDaVolta="Voltar ao índice"
          dispatch={dispatch}
        />
      )}
      {catalogo !== undefined && (
        <AcoDaPecaDaFundacao
          peca={escada}
          rotulo="escada"
          fala={FALA_DA_ESCADA}
          base={taxaDeBaseDaPeca(catalogo, 'ESCADA')}
          daPlanilha={daPlanilha}
          dispatch={dispatch}
          media={mediaDoAco}
          travadaPor={acoAprovadoDoElemento(aprovados, catalogo, 'ESCADA')}
        />
      )}

      {editou && erro !== undefined && <Recusa erro={erro} />}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={desenhando === 'LANCE' ? 'primary' : 'secondary'}
          size="sm"
          aria-pressed={desenhando === 'LANCE'}
          onClick={() => onDesenhar('LANCE')}
        >
          Lance
        </Button>
        <Button
          variant={desenhando === 'PATAMAR' ? 'primary' : 'secondary'}
          size="sm"
          aria-pressed={desenhando === 'PATAMAR'}
          onClick={() => onDesenhar('PATAMAR')}
        >
          Patamar
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_STAIR', pieceId: escada.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir escada
        </Button>
      </div>
    </section>
  );
}

/** Editar o lance clicado: degraus, espelho, piso e espessura, as contas dele, e excluir. */
export function LanceSelecionado({
  escada,
  indice,
  erro,
  dispatch: despachar,
  onVoltar,
  onFechar,
}: {
  escada: Stair;
  indice: number;
  erro: string | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** Volta a selecionar a escada inteira. */
  onVoltar: () => void;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const lance = escada.flights[indice];
  if (lance === undefined) return null;

  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const base = `escada-${escada.id}-lance-${String(indice)}`;
  const unico = escada.flights.length === 1;
  const emCm = (metrosDoCampo: number): number => Math.round(metrosDoCampo * 1000) / 10;
  const atualizar = (campos: {
    steps?: number;
    riser?: number;
    tread?: number;
    thickness?: number;
  }): void =>
    dispatch({ type: 'UPDATE_STAIR_FLIGHT', pieceId: escada.id, index: indice, ...campos });

  return (
    <section
      aria-label={`Lance ${String(indice + 1)} de ${escada.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Lance {indice + 1} de {escada.name}
        </h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Degraus" htmlFor={`${base}-degraus`}>
          <NumeroAoTerminar
            id={`${base}-degraus`}
            valor={lance.steps}
            className="w-20"
            onCommit={(steps) => atualizar({ steps })}
          />
        </Field>
        <Field label="Espelho (cm)" htmlFor={`${base}-espelho`}>
          <NumeroAoTerminar
            id={`${base}-espelho`}
            valor={emCm(lance.riser)}
            className="w-20"
            onCommit={(valor) => atualizar({ riser: emMetros(valor) })}
          />
        </Field>
        <Field label="Piso (cm)" htmlFor={`${base}-piso`}>
          <NumeroAoTerminar
            id={`${base}-piso`}
            valor={emCm(lance.tread)}
            className="w-20"
            onCommit={(valor) => atualizar({ tread: emMetros(valor) })}
          />
        </Field>
        <Field label="Espessura (cm)" htmlFor={`${base}-espessura`}>
          <NumeroAoTerminar
            id={`${base}-espessura`}
            valor={emCm(lance.thickness)}
            className="w-20"
            onCommit={(valor) => atualizar({ thickness: emMetros(valor) })}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Largura" valor={`${metros(flightWidth(lance))} m`} />
        <Linha rotulo="Altura vencida" valor={`${metros(flightRise(lance))} m`} />
        <Linha
          rotulo="Espelho × piso"
          valor={`${emTextoDeCentimetros(emCm(lance.riser))} × ${emTextoDeCentimetros(emCm(lance.tread))}`}
        />
        <Linha
          rotulo="Concreto do lance"
          valor={`${metros(stairVolume({ flights: [lance], landings: [] }), 3)} m³`}
        />
      </div>

      {editou && erro !== undefined && <Recusa erro={erro} />}
      {unico && (
        <p className="text-xs text-(--color-content-muted)">
          É o único lance: a escada precisa de ao menos um. Para tirá-lo, exclua a escada.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onVoltar}>
          Voltar à escada
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={unico}
          onClick={() => {
            dispatch({ type: 'REMOVE_STAIR_FLIGHT', pieceId: escada.id, index: indice });
            onVoltar();
          }}
        >
          <Trash2 className="size-4" /> Excluir lance
        </Button>
      </div>
    </section>
  );
}

/** Editar o patamar clicado: a espessura, a área e o concreto dele, e excluir. */
export function PatamarSelecionado({
  escada,
  indice,
  erro,
  dispatch: despachar,
  onVoltar,
  onFechar,
}: {
  escada: Stair;
  indice: number;
  erro: string | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onVoltar: () => void;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const patamar = escada.landings[indice];
  if (patamar === undefined) return null;

  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const area = polygonArea(patamar.outline);

  return (
    <section
      aria-label={`Patamar ${String(indice + 1)} de ${escada.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Patamar {indice + 1} de {escada.name}
        </h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <Field
        label="Espessura do patamar (cm)"
        htmlFor={`escada-${escada.id}-patamar-${String(indice)}`}
      >
        <NumeroAoTerminar
          id={`escada-${escada.id}-patamar-${String(indice)}`}
          valor={Math.round(patamar.thickness * 1000) / 10}
          className="w-24"
          onCommit={(valor) =>
            dispatch({
              type: 'UPDATE_STAIR_LANDING',
              pieceId: escada.id,
              index: indice,
              thickness: emMetros(valor),
            })
          }
        />
      </Field>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Área do patamar" valor={`${metros(area)} m²`} />
        <Linha rotulo="Concreto do patamar" valor={`${metros(area * patamar.thickness, 3)} m³`} />
      </div>

      {editou && erro !== undefined && <Recusa erro={erro} />}

      <div className="flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onVoltar}>
          Voltar à escada
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_STAIR_LANDING', pieceId: escada.id, index: indice });
            onVoltar();
          }}
        >
          <Trash2 className="size-4" /> Excluir patamar
        </Button>
      </div>
    </section>
  );
}
